import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import GSTReconciliationModal from "./GSTReconciliationModal";
import {
  BarChart3, Download, FileJson, FileSpreadsheet, FileCode2, Loader2,
  TrendingUp, TrendingDown, ArrowRightLeft, Receipt, ShoppingCart,
  Calculator, Scale, AlertTriangle, Gauge, Globe, CheckCircle2, History,
  ExternalLink, Ban, RotateCcw, ChevronRight, ChevronLeft, HardDrive,
  Eye, XCircle,
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const GST_TABS = [
  { id: "gst", label: "GST Liability", icon: Scale },
  { id: "gstr1", label: "GSTR-1", icon: FileJson },
  { id: "hsn", label: "HSN Table 12", icon: BarChart3 },
  { id: "export_refunds", label: "Export Refunds", icon: Globe },
  { id: "sales", label: "Sales", icon: TrendingUp },
  { id: "purchases", label: "Purchases", icon: ShoppingCart },
  { id: "pnl", label: "P&L", icon: Calculator },
  { id: "tally", label: "Tally Export", icon: FileCode2 },
];

const COMPOSITION_TABS = [
  { id: "cmp08", label: "CMP-08", icon: FileJson },
  { id: "sales", label: "Sales", icon: TrendingUp },
  { id: "purchases", label: "Purchases", icon: ShoppingCart },
  { id: "pnl", label: "P&L", icon: Calculator },
  { id: "tally", label: "Tally Export", icon: FileCode2 },
];

const UNREGISTERED_TABS = [
  { id: "turnover", label: "Turnover Tracker", icon: Gauge },
  { id: "sales", label: "Sales", icon: TrendingUp },
  { id: "purchases", label: "Purchases", icon: ShoppingCart },
  { id: "pnl", label: "P&L", icon: Calculator },
  { id: "tally", label: "Tally Export", icon: FileCode2 },
];

function getFYDefaults() {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  if (m < 4) return { from: `${y - 1}-04-01`, to: `${y}-03-31` };
  return { from: `${y}-04-01`, to: `${y + 1}-03-31` };
}

export default function ReportsPage() {
  const { user } = useAuth();
  const gstStatus = user?.gst_status || "registered_regular";
  const TABS = gstStatus === "registered_composition" ? COMPOSITION_TABS : gstStatus === "unregistered" ? UNREGISTERED_TABS : GST_TABS;
  const defaultTab = TABS[0].id;
  const [tab, setTab] = useState(defaultTab);
  const fy = getFYDefaults();
  const [fromDate, setFromDate] = useState(fy.from);
  const [toDate, setToDate] = useState(fy.to);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setData(null);
    const endpoints = {
      gst: "/reports/gst-liability",
      gstr1: "/reports/gstr1",
      hsn: "/reports/gstr1-hsn",
      export_refunds: "/reports/export-refunds",
      sales: "/reports/sales",
      purchases: "/reports/purchases",
      pnl: "/reports/pnl",
      tally: "/reports/pnl",
      cmp08: "/reports/cmp08",
      turnover: "/reports/turnover-tracker",
    };
    try {
      const endpoint = endpoints[tab];
      const url = (tab === "turnover" || tab === "export_refunds") ? endpoint : `${endpoint}?from_date=${fromDate}&to_date=${toDate}`;
      const r = await api.get(url);
      setData(r.data);
    } catch {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [tab, fromDate, toDate]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const [validationErrors, setValidationErrors] = useState(null); // {message, errors, error_count}

  const downloadFile = async (path, filename) => {
    // Fetch-based so we can catch 400 responses with schema-validation errors
    // and surface each failing invoice/field in a dedicated modal. Anchor-tag
    // downloads swallow non-200s and just show a blank page.
    try {
      const r = await api.get(`${path}?from_date=${fromDate}&to_date=${toDate}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloading ${filename}`);
    } catch (err) {
      // axios wraps blob-typed responses. Try to parse the error body as JSON.
      let detail = err.response?.data;
      if (detail instanceof Blob) {
        try {
          const text = await detail.text();
          detail = JSON.parse(text)?.detail || {};
        } catch { detail = {}; }
      } else {
        detail = detail?.detail || detail || {};
      }
      if (detail?.errors && Array.isArray(detail.errors)) {
        setValidationErrors(detail);
      } else {
        toast.error(detail?.message || detail?.detail || err.message || "Download failed");
      }
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Reports & Data</h1>
          <p className="text-sm text-[#64748b] mt-1">GST 2.0 compliance-ready reports and exports</p>
        </div>
        <div className="flex items-center gap-2">
          <div>
            <Label className="text-xs text-[#64748b]">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              data-testid="report-from-date" className="h-9 w-36 border-[#e2e8f0] text-sm" />
          </div>
          <div>
            <Label className="text-xs text-[#64748b]">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              data-testid="report-to-date" className="h-9 w-36 border-[#e2e8f0] text-sm" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="report-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`tab-${t.id}`}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              tab === t.id
                ? "bg-[#4338ca] text-white shadow-md shadow-[#4338ca]/20"
                : "bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0] hover:text-[#0f172a]"
            }`}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-[#4338ca]" />
          <span className="ml-2 text-sm text-[#64748b]">Loading report...</span>
        </div>
      ) : (
        <>
          {tab === "gst" && data && <GSTLiabilityView data={data} fromDate={fromDate} toDate={toDate} />}
          {tab === "gstr1" && data && <GSTR1View data={data} onDownload={downloadFile} fromDate={fromDate} toDate={toDate} />}
          {tab === "hsn" && data && <HSNView data={data} />}
          {tab === "sales" && data && <SalesView data={data} />}
          {tab === "purchases" && data && <PurchasesView data={data} gstStatus={gstStatus} />}
          {tab === "pnl" && data && <PnLView data={data} />}
          {tab === "tally" && <TallyView fromDate={fromDate} toDate={toDate} data={data} />}
          {tab === "export_refunds" && data && <ExportRefundsView data={data} onRefresh={fetchReport} />}
          {tab === "turnover" && data && <TurnoverTrackerPanel data={data} />}
          {tab === "cmp08" && data && <CMP08Panel data={data} />}
        </>
      )}

      <GSTR1ValidationErrorDialog
        open={!!validationErrors}
        onOpenChange={() => setValidationErrors(null)}
        errors={validationErrors}
      />
    </div>
  );
}

function GSTR1ValidationErrorDialog({ open, onOpenChange, errors }) {
  if (!errors) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[820px] max-h-[85vh] overflow-hidden flex flex-col" data-testid="gstr1-validation-error-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl flex items-center gap-2 text-[#ef4444]">
            <AlertTriangle className="h-5 w-5" />
            GSTR-1 payload rejected by schema
          </DialogTitle>
          <DialogDescription className="text-sm text-[#64748b]">
            {errors.message || "The compiled GSTR-1 JSON failed department-schema validation."} Fix the flagged invoices/fields below and retry.
            <span className="ml-1 text-[11px] text-[#94a3b8]">Schema: {errors.schema_version || "GST2.2"} · {errors.error_count} issue{errors.error_count === 1 ? "" : "s"} found.</span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto -mx-6 px-6" data-testid="gstr1-validation-error-list">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">Invoice</th>
                <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">Customer GSTIN</th>
                <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">Field</th>
                <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">Problem</th>
              </tr>
            </thead>
            <tbody>
              {errors.errors.map((e, i) => (
                <tr key={i} className="border-b border-[#f1f5f9] hover:bg-[#fef2f2]" data-testid={`gstr1-error-row-${i}`}>
                  <td className="py-2 px-2 text-[#0f172a] font-mono text-xs">
                    {e.invoice_number || "—"}
                    {e.invoice_date && <div className="text-[10px] text-[#94a3b8]">{e.invoice_date}</div>}
                  </td>
                  <td className="py-2 px-2 text-[#64748b] font-mono text-xs">{e.ctin || "—"}</td>
                  <td className="py-2 px-2 text-[#4338ca] font-mono text-xs">{e.field}</td>
                  <td className="py-2 px-2 text-[#ef4444] text-xs">{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GSTLiabilityView({ data, fromDate, toDate }) {
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillSource, setDrillSource] = useState("all"); // all | expense | purchase
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [drillFilter, setDrillFilter] = useState("");
  const [showBlocked, setShowBlocked] = useState(true);
  const [reconOpen, setReconOpen] = useState(false);

  const openDrill = useCallback(async (source) => {
    setDrillSource(source);
    setDrillOpen(true);
    setDrillLoading(true);
    setDrillData(null);
    setDrillFilter("");
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate, source });
      const r = await api.get(`/reports/gst-liability/itc-details?${params.toString()}`);
      setDrillData(r.data);
    } catch {
      toast.error("Failed to load ITC details");
    } finally {
      setDrillLoading(false);
    }
  }, [fromDate, toDate]);

  const exportDrillCsv = async () => {
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate, source: drillSource });
      const r = await api.get(`/reports/gst-liability/itc-details.csv?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `itc_details_${fromDate}_to_${toDate}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("ITC details exported");
    } catch {
      toast.error("CSV export failed");
    }
  };

  if (!data || !data.output_tax) return null;

  const filteredRows = (drillData?.rows || []).filter((r) => {
    if (!showBlocked && r.itc_amount === 0) return false;
    if (!drillFilter) return true;
    const q = drillFilter.toLowerCase();
    return (
      (r.vendor_name || "").toLowerCase().includes(q) ||
      (r.bill_number || "").toLowerCase().includes(q) ||
      (r.gst_number || "").toLowerCase().includes(q) ||
      (r.category || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4" data-testid="gst-liability-view">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard icon={TrendingUp} color="#4338ca" label="Output Tax (Sales)" value={formatINR(data.output_tax.total)}
          sub={`${data.invoice_count} invoices`} testId="output-tax-card" />
        <button
          type="button"
          onClick={() => openDrill("all")}
          className="text-left group focus:outline-none focus:ring-2 focus:ring-[#10b981]/30 rounded-2xl transition"
          data-testid="itc-card-clickable"
          title="Click to see every expense & purchase contributing to ITC"
        >
          <MetricCard
            icon={TrendingDown}
            color="#10b981"
            label={<span className="inline-flex items-center gap-1.5">Input Tax Credit <ChevronRight className="h-3.5 w-3.5 opacity-40 group-hover:opacity-100 group-hover:translate-x-0.5 transition" /></span>}
            value={formatINR(data.input_tax_credit.total)}
            sub={`${data.expense_count || 0} expenses · ${data.purchase_count || 0} purchases · Click to drill down`}
            testId="itc-card"
          />
        </button>
        <MetricCard icon={ArrowRightLeft} color={data.net_liability >= 0 ? "#ef4444" : "#10b981"}
          label="Net GST Liability" value={formatINR(data.net_liability)}
          sub={data.net_liability >= 0 ? "Payable to Govt" : "Refundable"} testId="net-liability-card" />
      </div>
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-5">
          <h3 className="font-outfit text-sm font-semibold text-[#0f172a] mb-3">Tax Breakdown</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="space-y-2">
              <p className="text-xs text-[#64748b] font-medium">CGST</p>
              <p className="font-mono font-bold text-[#0f172a]">{formatINR(data.output_tax.cgst)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-[#64748b] font-medium">SGST</p>
              <p className="font-mono font-bold text-[#0f172a]">{formatINR(data.output_tax.sgst)}</p>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-[#64748b] font-medium">IGST</p>
              <p className="font-mono font-bold text-[#0f172a]">{formatINR(data.output_tax.igst)}</p>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-[#e2e8f0] grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-[#64748b]">Total Sales (Taxable)</p>
              <p className="font-mono font-semibold text-[#0f172a]">{formatINR(data.total_sales)}</p>
            </div>
            <div>
              <p className="text-xs text-[#64748b]">Total Purchases</p>
              <p className="font-mono font-semibold text-[#0f172a]">{formatINR(data.total_purchases)}</p>
            </div>
          </div>
          {data.input_tax_credit && (data.input_tax_credit.from_expenses !== undefined || data.input_tax_credit.from_purchases !== undefined) && (
            <div className="mt-4 pt-3 border-t border-[#e2e8f0]" data-testid="itc-breakdown">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-[#64748b] font-medium">ITC Sources</p>
                <button
                  type="button"
                  onClick={() => openDrill("all")}
                  className="text-[11px] text-[#4338ca] hover:text-[#3730a3] font-medium inline-flex items-center gap-1"
                  data-testid="itc-view-all-details"
                >
                  View all details <ChevronRight className="h-3 w-3" />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <button
                  type="button"
                  onClick={() => openDrill("expense")}
                  className="text-left rounded-lg border border-[#e2e8f0] hover:border-[#4338ca]/40 hover:bg-[#4338ca]/5 p-3 transition group"
                  data-testid="itc-drill-expenses-btn"
                >
                  <p className="text-xs text-[#64748b] inline-flex items-center gap-1">
                    From Expenses <ChevronRight className="h-3 w-3 opacity-40 group-hover:opacity-100 transition" />
                  </p>
                  <p className="font-mono font-semibold text-[#0f172a] mt-1" data-testid="itc-from-expenses">
                    {formatINR(data.input_tax_credit.from_expenses || 0)}
                  </p>
                  <p className="text-[10px] text-[#94a3b8] mt-0.5">{data.expense_count || 0} expenses</p>
                </button>
                <button
                  type="button"
                  onClick={() => openDrill("purchase")}
                  className="text-left rounded-lg border border-[#e2e8f0] hover:border-[#4338ca]/40 hover:bg-[#4338ca]/5 p-3 transition group"
                  data-testid="itc-drill-purchases-btn"
                >
                  <p className="text-xs text-[#64748b] inline-flex items-center gap-1">
                    From Purchases (COGS) <ChevronRight className="h-3 w-3 opacity-40 group-hover:opacity-100 transition" />
                  </p>
                  <p className="font-mono font-semibold text-[#0f172a] mt-1" data-testid="itc-from-purchases">
                    {formatINR(data.input_tax_credit.from_purchases || 0)}
                  </p>
                  <p className="text-[10px] text-[#94a3b8] mt-0.5">{data.purchase_count || 0} purchases</p>
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ITC Drill-down Modal */}
      <Dialog open={drillOpen} onOpenChange={setDrillOpen}>
        <DialogContent className="sm:max-w-[1100px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="itc-drilldown-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-[#10b981]" />
              Input Tax Credit — Details
            </DialogTitle>
            <DialogDescription className="text-sm text-[#64748b]">
              {fromDate} → {toDate} · {drillSource === "all" ? "All sources" : drillSource === "expense" ? "Expenses only" : "Purchases only"}
            </DialogDescription>
          </DialogHeader>

          {/* Source pills + filter */}
          <div className="flex flex-wrap items-center gap-2 pt-2 pb-3 border-b border-[#e2e8f0]">
            {[
              { id: "all", label: "All Sources" },
              { id: "expense", label: "Expenses" },
              { id: "purchase", label: "Purchases" },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => openDrill(s.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
                  drillSource === s.id
                    ? "bg-[#4338ca] text-white"
                    : "bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]"
                }`}
                data-testid={`itc-source-pill-${s.id}`}
              >
                {s.label}
              </button>
            ))}
            <Input
              value={drillFilter}
              onChange={(e) => setDrillFilter(e.target.value)}
              placeholder="Filter vendor / bill # / GSTIN / category"
              className="h-8 text-sm w-64 ml-2"
              data-testid="itc-drill-filter"
            />
            <label className="flex items-center gap-1.5 text-xs text-[#64748b] cursor-pointer ml-2">
              <Checkbox checked={showBlocked} onCheckedChange={(v) => setShowBlocked(!!v)} data-testid="itc-show-blocked" />
              Show ineligible rows
            </label>
            <div className="ml-auto flex items-center gap-2">
              {drillData && (
                <span className="text-xs text-[#64748b]" data-testid="itc-drill-total">
                  Total: <span className="font-mono font-semibold text-[#10b981]">{formatINR(drillData.totals.itc_total)}</span>
                </span>
              )}
              <Button size="sm" variant="outline" onClick={exportDrillCsv} disabled={!drillData || drillLoading}
                data-testid="itc-drill-export-csv" className="text-xs h-8">
                <Download className="h-3 w-3 mr-1" /> CSV
              </Button>
              <Button size="sm" onClick={() => setReconOpen(true)}
                data-testid="itc-open-reconciliation-btn"
                className="text-xs h-8 bg-[#4338ca] hover:bg-[#3730a3]">
                <ArrowRightLeft className="h-3 w-3 mr-1" /> Reconcile with GSTR-2B
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="flex-1 overflow-auto -mx-6 px-6">
            {drillLoading ? (
              <div className="py-16 flex items-center justify-center text-sm text-[#64748b]">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading ITC details…
              </div>
            ) : !drillData || filteredRows.length === 0 ? (
              <div className="py-16 text-center text-sm text-[#94a3b8]" data-testid="itc-drill-empty">
                {drillFilter ? "No rows match your filter." : "No ITC-eligible expenses or purchases in this period."}
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="itc-drill-table">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    <th className="text-left py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Source</th>
                    <th className="text-left py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Date</th>
                    <th className="text-left py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Vendor</th>
                    <th className="text-left py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Bill No.</th>
                    <th className="text-left py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Category</th>
                    <th className="text-right py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">Taxable</th>
                    <th className="text-right py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">CGST</th>
                    <th className="text-right py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">SGST</th>
                    <th className="text-right py-2.5 px-2 text-[10px] text-[#64748b] font-semibold uppercase tracking-wider">IGST</th>
                    <th className="text-right py-2.5 px-2 text-[10px] text-[#10b981] font-semibold uppercase tracking-wider">ITC</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => {
                    const isBlocked = r.itc_amount === 0 && r.blocked_reason;
                    return (
                      <tr
                        key={`${r.source}-${r.id}`}
                        className={`border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc] ${isBlocked ? "opacity-60" : ""}`}
                        data-testid={`itc-row-${r.source}-${r.id}`}
                      >
                        <td className="py-2.5 px-2">
                          <Badge className={r.source === "expense"
                            ? "bg-[#4338ca]/10 text-[#4338ca] text-[9px] px-1.5"
                            : "bg-[#f59e0b]/10 text-[#f59e0b] text-[9px] px-1.5"}>
                            {r.source === "expense" ? "Expense" : "Purchase"}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-2 text-[#64748b] whitespace-nowrap">{r.date}</td>
                        <td className="py-2.5 px-2 text-[#0f172a] font-medium">
                          <div className="flex items-center gap-1.5">
                            <span>{r.vendor_name || "—"}</span>
                            {r.receipt_drive_link && (
                              <a href={r.receipt_drive_link} target="_blank" rel="noopener noreferrer"
                                className="text-[#4338ca] hover:text-[#3730a3]" title="View receipt in Drive">
                                <HardDrive className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                          {r.gst_number && <div className="text-[10px] text-[#94a3b8] font-mono">{r.gst_number}</div>}
                          {isBlocked && (
                            <div className="text-[10px] text-red-500 mt-0.5 inline-flex items-center gap-1">
                              {r.reverse_charge ? <RotateCcw className="h-2.5 w-2.5" /> : <Ban className="h-2.5 w-2.5" />}
                              {r.blocked_reason}
                            </div>
                          )}
                        </td>
                        <td className="py-2.5 px-2 text-[#64748b] font-mono text-xs">{r.bill_number || "—"}</td>
                        <td className="py-2.5 px-2 text-[#64748b] text-xs">{r.category || "—"}</td>
                        <td className="py-2.5 px-2 text-right font-mono tabular-nums text-[#0f172a]">{formatINR(r.taxable_value)}</td>
                        <td className="py-2.5 px-2 text-right font-mono tabular-nums text-[#64748b]">{r.cgst ? formatINR(r.cgst) : "—"}</td>
                        <td className="py-2.5 px-2 text-right font-mono tabular-nums text-[#64748b]">{r.sgst ? formatINR(r.sgst) : "—"}</td>
                        <td className="py-2.5 px-2 text-right font-mono tabular-nums text-[#64748b]">{r.igst ? formatINR(r.igst) : "—"}</td>
                        <td className="py-2.5 px-2 text-right font-mono tabular-nums font-semibold text-[#10b981]">
                          {r.itc_amount > 0 ? formatINR(r.itc_amount) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                {drillData && (
                  <tfoot className="sticky bottom-0 bg-white">
                    <tr className="border-t-2 border-[#e2e8f0] bg-[#f8fafc]">
                      <td colSpan={5} className="py-3 px-2 text-xs font-semibold text-[#0f172a]">
                        Total ({drillData.totals.eligible_count} eligible / {drillData.totals.row_count} rows)
                      </td>
                      <td className="py-3 px-2 text-right font-mono font-bold text-[#0f172a]">—</td>
                      <td className="py-3 px-2 text-right font-mono font-bold text-[#0f172a]" data-testid="itc-drill-total-cgst">{formatINR(drillData.totals.cgst)}</td>
                      <td className="py-3 px-2 text-right font-mono font-bold text-[#0f172a]" data-testid="itc-drill-total-sgst">{formatINR(drillData.totals.sgst)}</td>
                      <td className="py-3 px-2 text-right font-mono font-bold text-[#0f172a]" data-testid="itc-drill-total-igst">{formatINR(drillData.totals.igst)}</td>
                      <td className="py-3 px-2 text-right font-mono font-bold text-[#10b981]" data-testid="itc-drill-total-itc">{formatINR(drillData.totals.itc_total)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <GSTReconciliationModal
        open={reconOpen}
        onOpenChange={setReconOpen}
        fromDate={fromDate}
        toDate={toDate}
      />
    </div>
  );
}

function GSTR1View({ data, onDownload, fromDate, toDate }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [expandedInvoice, setExpandedInvoice] = useState(null);

  const runPreview = useCallback(async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    setExpandedInvoice(null);
    try {
      const r = await api.get(`/reports/gstr1/preview?from_date=${fromDate}&to_date=${toDate}`);
      setPreviewData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Preview failed");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }, [fromDate, toDate]);

  if (!data || !data.b2b) return null;
  return (
    <div className="space-y-4" data-testid="gstr1-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Badge className="bg-[#4338ca] text-white">B2B: {data.b2b_count}</Badge>
          <Badge className="bg-[#f59e0b] text-white">B2C: {data.b2c_count}</Badge>
          <span className="text-sm text-[#64748b]">Total Taxable: {formatINR(data.total_taxable)}</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={runPreview}
            data-testid="preview-gstr1-btn" className="rounded-lg text-xs border-[#4338ca]/40 text-[#4338ca] hover:bg-[#4338ca]/5">
            <Eye className="h-3.5 w-3.5 mr-1" /> Preview & Dry-Run
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDownload("/reports/gstr1/download-json", "GSTR1.json")}
            data-testid="download-gstr1-json" className="rounded-lg text-xs">
            <FileJson className="h-3.5 w-3.5 mr-1" /> JSON
          </Button>
          <Button size="sm" onClick={() => onDownload("/reports/gstr1/download-excel", "GSTR1.xlsx")}
            data-testid="download-gstr1-excel" className="bg-[#10b981] hover:bg-[#059669] rounded-lg text-xs">
            <FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* B2B Table */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">B2B Invoices (Registered Parties)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <InvoiceTable invoices={data.b2b} showGstin />
        </CardContent>
      </Card>

      {/* B2C Table */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">B2C Invoices (Unregistered Parties)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <InvoiceTable invoices={data.b2c} />
        </CardContent>
      </Card>

      <GSTR1PreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        loading={previewLoading}
        preview={previewData}
        onDownload={() => onDownload("/reports/gstr1/download-json", "GSTR1.json")}
        expandedInvoice={expandedInvoice}
        setExpandedInvoice={setExpandedInvoice}
      />
    </div>
  );
}

function GSTR1PreviewDialog({ open, onOpenChange, loading, preview, onDownload, expandedInvoice, setExpandedInvoice }) {
  const ready = preview?.status === "ready";
  const c = preview?.counts || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1050px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="gstr1-preview-modal">
        <DialogHeader>
          <DialogTitle className="font-outfit text-xl flex items-center gap-2">
            <Eye className="h-5 w-5 text-[#4338ca]" />
            GSTR-1 Preview & Dry-Run
          </DialogTitle>
          <DialogDescription className="text-sm text-[#64748b]">
            {preview
              ? <>Period <span className="font-mono">{preview.period.fp}</span> · Supplier <span className="font-mono">{preview.supplier_gstin || "—"}</span> · Schema {preview.schema_version}</>
              : "Compiling + validating your GSTR-1 payload against the government schema…"}
          </DialogDescription>
        </DialogHeader>

        {loading || !preview ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 mr-2 animate-spin text-[#4338ca]" />
            <span className="text-sm text-[#64748b]">Running validator…</span>
          </div>
        ) : (
          <>
            {/* Status header */}
            <div className={`rounded-xl border p-4 ${ready ? "border-[#10b981]/30 bg-[#10b981]/5" : "border-[#ef4444]/30 bg-[#ef4444]/5"}`}
                 data-testid="gstr1-preview-status">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  {ready ? (
                    <CheckCircle2 className="h-5 w-5 text-[#10b981]" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-[#ef4444]" />
                  )}
                  <div>
                    <p className={`font-outfit text-lg font-bold ${ready ? "text-[#10b981]" : "text-[#ef4444]"}`}>
                      {ready ? "Ready to file" : "Schema validation failed"}
                    </p>
                    <p className="text-xs text-[#64748b]">
                      {c.ready} / {c.total} invoices passing
                      {c.invalid > 0 && <> · <span className="text-[#ef4444]">{c.invalid} with errors</span></>}
                      {c.root_errors > 0 && <> · <span className="text-[#ef4444]">{c.root_errors} payload-level issues</span></>}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={onDownload}
                  disabled={!ready}
                  data-testid="gstr1-preview-download-btn"
                  className={ready ? "bg-[#10b981] hover:bg-[#059669]" : ""}
                >
                  <FileJson className="h-3.5 w-3.5 mr-1.5" />
                  {ready ? "Download JSON" : "Fix errors to enable"}
                </Button>
              </div>
            </div>

            {/* Payload-level (root) errors */}
            {preview.root_errors?.length > 0 && (
              <div className="rounded-lg border border-[#ef4444]/30 bg-[#fef2f2] p-3 space-y-1" data-testid="gstr1-preview-root-errors">
                <p className="text-xs font-semibold text-[#ef4444] uppercase tracking-wider">Payload-level issues</p>
                <ul className="text-xs text-[#0f172a] space-y-0.5 pl-4 list-disc">
                  {preview.root_errors.map((e, i) => (
                    <li key={i}>
                      <span className="font-mono text-[#4338ca]">{e.field}</span> — {e.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Per-invoice table */}
            <div className="flex-1 overflow-auto -mx-6 px-6">
              {preview.invoices.length === 0 ? (
                <div className="py-16 text-center text-sm text-[#94a3b8]" data-testid="gstr1-preview-empty">
                  No B2B invoices in this period.
                </div>
              ) : (
                <table className="w-full text-sm" data-testid="gstr1-preview-table">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                      <th className="py-2 px-2 w-10"></th>
                      <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">Invoice</th>
                      <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">Customer GSTIN</th>
                      <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">Date</th>
                      <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">POS</th>
                      <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">RCM</th>
                      <th className="text-right py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">Value</th>
                      <th className="text-left py-2 px-2 text-[10px] uppercase tracking-wider text-[#64748b] font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.invoices.map((row) => {
                      const key = `${row.ctin}-${row.invoice_number}`;
                      const isOk = row.status === "ready";
                      const isExpanded = expandedInvoice === key;
                      return (
                        <>
                          <tr
                            key={key}
                            className={`border-b border-[#f1f5f9] cursor-pointer hover:bg-[#f8fafc] ${isOk ? "" : "bg-[#fef2f2]/40"}`}
                            onClick={() => setExpandedInvoice(isExpanded ? null : key)}
                            data-testid={`gstr1-preview-row-${row.invoice_number}`}
                          >
                            <td className="py-2 px-2 text-[#94a3b8]">
                              {row.errors?.length > 0 && (
                                isExpanded
                                  ? <ChevronLeft className="h-3.5 w-3.5 rotate-90" />
                                  : <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </td>
                            <td className="py-2 px-2 text-[#0f172a] font-mono text-xs">{row.invoice_number}</td>
                            <td className="py-2 px-2 text-[#64748b] font-mono text-xs">{row.ctin}</td>
                            <td className="py-2 px-2 text-[#64748b] text-xs whitespace-nowrap">{row.invoice_date}</td>
                            <td className="py-2 px-2 text-[#64748b] font-mono text-xs">{row.pos || "—"}</td>
                            <td className="py-2 px-2 text-[#64748b] font-mono text-xs">{row.rchrg}</td>
                            <td className="py-2 px-2 text-right font-mono tabular-nums text-[#0f172a]">{formatINR(row.invoice_value)}</td>
                            <td className="py-2 px-2">
                              {isOk ? (
                                <Badge className="bg-[#10b981]/10 text-[#10b981] text-[10px]" data-testid={`gstr1-status-ready-${row.invoice_number}`}>
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Ready
                                </Badge>
                              ) : (
                                <Badge className="bg-[#ef4444]/10 text-[#ef4444] text-[10px]" data-testid={`gstr1-status-invalid-${row.invoice_number}`}>
                                  <XCircle className="h-3 w-3 mr-1" /> {row.errors.length} issue{row.errors.length === 1 ? "" : "s"}
                                </Badge>
                              )}
                            </td>
                          </tr>
                          {isExpanded && row.errors?.length > 0 && (
                            <tr className="bg-[#fef2f2]" data-testid={`gstr1-preview-detail-${row.invoice_number}`}>
                              <td colSpan={8} className="py-3 px-4">
                                <p className="text-[10px] font-semibold text-[#ef4444] uppercase tracking-wider mb-1.5">Issues on this invoice</p>
                                <ul className="text-xs text-[#0f172a] space-y-1">
                                  {row.errors.map((e, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                      <span className="font-mono text-[#4338ca] shrink-0">{e.field}</span>
                                      <span className="text-[#ef4444]">— {e.message}</span>
                                    </li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function HSNView({ data }) {
  if (!data || !data.tab_a_b2b) return null;
  return (
    <div className="space-y-4" data-testid="hsn-view">
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Table 12A — HSN Summary (B2B)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <HSNTable entries={data.tab_a_b2b} testId="hsn-b2b-table" />
        </CardContent>
      </Card>
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Table 12B — HSN Summary (B2C)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <HSNTable entries={data.tab_b_b2c} testId="hsn-b2c-table" />
        </CardContent>
      </Card>
    </div>
  );
}

function SalesView({ data }) {
  if (!data || !data.invoices) return null;
  return (
    <div className="space-y-4" data-testid="sales-view">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <MetricCard icon={TrendingUp} color="#4338ca" label="Total Sales" value={formatINR(data.total)} sub={`${data.count} invoices`} testId="sales-total-card" />
        <MetricCard icon={Receipt} color="#f59e0b" label="Total Tax Collected" value={formatINR(data.total_tax)} testId="sales-tax-card" />
        <MetricCard icon={BarChart3} color="#10b981" label="Period" value={`${data.period.from} to ${data.period.to}`} testId="sales-period-card" />
      </div>
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          <InvoiceTable invoices={data.invoices} showStatus />
        </CardContent>
      </Card>
    </div>
  );
}

function PurchasesView({ data, gstStatus }) {
  if (!data || !data.expenses) return null;
  return (
    <div className="space-y-4" data-testid="purchases-view">
      {gstStatus === "registered_composition" && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800"><strong>Note:</strong> You are registered under the Composition Scheme. Input Tax Credit (ITC) is not applicable on your purchases.</p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <MetricCard icon={ShoppingCart} color="#ef4444" label="Total Purchases" value={formatINR(data.total)} sub={`${data.count} expenses`} testId="purchases-total-card" />
        <MetricCard icon={BarChart3} color="#64748b" label="Period" value={`${data.period.from} to ${data.period.to}`} testId="purchases-period-card" />
      </div>
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {data.expenses.length === 0 ? (
            <p className="p-8 text-center text-sm text-[#64748b]">No expenses in this period.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Vendor</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">Category</th>
                    <th className="text-left py-3 px-4 text-[#64748b] font-medium">GST No.</th>
                    <th className="text-right py-3 px-4 text-[#64748b] font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.expenses.map((e, i) => (
                    <tr key={i} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]">
                      <td className="py-2.5 px-4 text-[#0f172a] font-medium">{e.vendor_name}</td>
                      <td className="py-2.5 px-4 text-[#64748b]">{e.date}</td>
                      <td className="py-2.5 px-4 text-[#64748b]">{e.category || "-"}</td>
                      <td className="py-2.5 px-4 text-[#64748b] font-mono text-xs">{e.gst_number || "-"}</td>
                      <td className="py-2.5 px-4 text-right font-mono font-semibold text-[#0f172a]">{formatINR(e.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function PnLView({ data }) {
  if (!data || !data.income) return null;
  const hasCogs = data.direct_costs && data.direct_costs.total > 0;
  return (
    <div className="space-y-4" data-testid="pnl-view">
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        <MetricCard icon={TrendingUp} color="#4338ca" label="Revenue" value={formatINR(data.income.total)} sub={`${data.income.invoice_count} invoices`} testId="pnl-revenue" />
        {hasCogs && <MetricCard icon={ShoppingCart} color="#f59e0b" label="COGS" value={formatINR(data.direct_costs.total)} sub={`${data.direct_costs.purchase_count} purchases`} testId="pnl-cogs" />}
        {hasCogs && <MetricCard icon={TrendingUp} color={data.gross_profit >= 0 ? "#10b981" : "#ef4444"} label="Gross Profit" value={formatINR(data.gross_profit)} testId="pnl-gross" />}
        <MetricCard icon={TrendingDown} color="#ef4444" label="Overheads" value={formatINR(data.expenses.total)} sub={`${data.expenses.expense_count} bills`} testId="pnl-expenses" />
        <MetricCard icon={Calculator} color={data.net_profit >= 0 ? "#10b981" : "#ef4444"} label="Net Profit" value={formatINR(data.net_profit)} testId="pnl-net" />
      </div>

      <div className={`grid grid-cols-1 ${hasCogs ? "md:grid-cols-3" : "md:grid-cols-2"} gap-4`}>
        {/* Income section */}
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#10b981] flex items-center gap-1.5">
              <TrendingUp className="h-4 w-4" /> Income
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between text-sm py-2 border-b border-[#f1f5f9]">
              <span className="text-[#64748b]">Sales Revenue</span>
              <span className="font-mono font-semibold text-[#0f172a]">{formatINR(data.income.total)}</span>
            </div>
            <div className="flex justify-between text-sm py-2 font-bold border-t border-[#0f172a] mt-2">
              <span>Total Income</span>
              <span className="font-mono text-[#10b981]">{formatINR(data.income.total)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Direct Costs (COGS) */}
        {hasCogs && (
          <Card className="bg-white border-[#e2e8f0] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold text-[#f59e0b] flex items-center gap-1.5">
                <ShoppingCart className="h-4 w-4" /> Direct Costs (COGS)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data.direct_costs.by_category && Object.entries(data.direct_costs.by_category).map(([cat, amt]) => (
                <div key={cat} className="flex justify-between text-sm py-1.5 border-b border-[#f1f5f9] last:border-0">
                  <span className="text-[#64748b]">{cat}</span>
                  <span className="font-mono text-[#0f172a]">{formatINR(amt)}</span>
                </div>
              ))}
              <div className="flex justify-between text-sm py-2 font-bold border-t border-[#0f172a] mt-2">
                <span>Total COGS</span>
                <span className="font-mono text-[#f59e0b]">{formatINR(data.direct_costs.total)}</span>
              </div>
              <div className="flex justify-between text-sm py-2 font-bold bg-[#f8fafc] rounded px-2 mt-1" data-testid="gross-profit-line">
                <span className="text-[#0f172a]">Gross Profit</span>
                <span className={`font-mono ${data.gross_profit >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}`}>{formatINR(data.gross_profit)}</span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Indirect Expenses (Overheads) */}
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-[#ef4444] flex items-center gap-1.5">
              <TrendingDown className="h-4 w-4" /> {hasCogs ? "Indirect Expenses (Overheads)" : "Expenses"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.expenses.by_category && Object.entries(data.expenses.by_category).map(([cat, amt]) => (
              <div key={cat} className="flex justify-between text-sm py-1.5 border-b border-[#f1f5f9] last:border-0">
                <span className="text-[#64748b]">{cat}</span>
                <span className="font-mono text-[#0f172a]">{formatINR(amt)}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm py-2 font-bold border-t border-[#0f172a] mt-2">
              <span>Total {hasCogs ? "Overheads" : "Expenses"}</span>
              <span className="font-mono text-[#ef4444]">{formatINR(data.expenses.total)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tax Payable line */}
      <Card className="bg-[#fef3c7] border-[#fbbf24] shadow-sm">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Scale className="h-5 w-5 text-[#92400e]" />
            <div>
              <p className="text-sm font-semibold text-[#92400e]">Tax Payable (GST)</p>
              <p className="text-xs text-[#a16207]">Output Tax {formatINR(data.output_tax)} - ITC {formatINR(data.input_tax_credit)}</p>
            </div>
          </div>
          <p className="font-outfit text-xl font-bold text-[#92400e]" data-testid="tax-payable-amount">{formatINR(data.tax_payable)}</p>
        </CardContent>
      </Card>

      {/* Net Profit */}
      <Card className={`shadow-sm ${data.net_profit >= 0 ? "bg-[#ecfdf5] border-[#10b981]" : "bg-red-50 border-red-300"}`}>
        <CardContent className="p-4 flex items-center justify-between">
          <p className="text-sm font-semibold text-[#0f172a]">Net Profit / (Loss)</p>
          <p className={`font-outfit text-2xl font-bold ${data.net_profit >= 0 ? "text-[#10b981]" : "text-[#ef4444]"}`}
            data-testid="net-profit-amount">{formatINR(data.net_profit)}</p>
        </CardContent>
      </Card>
    </div>
  );
}

const TALLY_MODULES = [
  { id: "sales", label: "Sales", desc: "Invoices → Sales vouchers", icon: TrendingUp },
  { id: "purchases", label: "Purchases", desc: "Bills → Purchase vouchers", icon: ShoppingCart },
  { id: "expenses", label: "Expenses", desc: "Bills → Journal / Payment vouchers", icon: Receipt },
  { id: "receipts", label: "Receipts", desc: "Customer payments in", icon: TrendingUp },
  { id: "payments", label: "Payments", desc: "Vendor payments out", icon: ArrowRightLeft },
];

function fmtDMY(s) {
  if (!s) return "";
  const parts = String(s).split("-");
  if (parts.length !== 3) return s;
  const [y, m, d] = parts;
  return `${d}-${m}-${y}`;
}

function fmtTimestamp(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString();
  } catch {
    return s;
  }
}

function TallyView({ fromDate, toDate, data }) {
  const [selected, setSelected] = useState({
    sales: true, purchases: true, expenses: true, receipts: true, payments: true,
  });
  const [purchasesHasInventory, setPurchasesHasInventory] = useState(true);
  const [history, setHistory] = useState([]);
  const [last, setLast] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get("/reports/tally-xml/history?limit=5");
      setHistory(r.data?.history || []);
      setLast(r.data?.last || null);
    } catch {
      // silent — history is non-critical
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const chosen = TALLY_MODULES.filter(m => selected[m.id]).map(m => m.id);
  const canDownload = chosen.length > 0;

  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));

  const handleDownload = () => {
    if (!canDownload) {
      toast.error("Select at least one module");
      return;
    }
    const modulesParam = chosen.join(",");
    const params = new URLSearchParams({
      from_date: fromDate,
      to_date: toDate,
      modules: modulesParam,
      purchases_has_inventory: String(purchasesHasInventory),
    });
    const url = `${API_BASE}/api/reports/tally-xml?${params.toString()}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `TallyExport_${fromDate}_${toDate}.xml`;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Downloading Tally XML");
    // Refresh history after download lands & backend writes the log row
    setTimeout(fetchHistory, 1800);
  };

  return (
    <div className="space-y-4" data-testid="tally-view">
      {/* Last export badge */}
      {last && (
        <div
          className="flex flex-wrap items-center gap-3 bg-[#eef2ff] border border-[#c7d2fe] rounded-xl px-4 py-3"
          data-testid="tally-last-export-badge"
        >
          <div className="h-9 w-9 rounded-lg bg-[#4338ca]/10 flex items-center justify-center">
            <History className="h-4 w-4 text-[#4338ca]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wide text-[#4338ca] font-semibold">
              Last Export Period
            </p>
            <p className="text-sm text-[#0f172a] font-medium" data-testid="tally-last-export-period">
              {fmtDMY(last.from_date)} → {fmtDMY(last.to_date)}
            </p>
            <p className="text-xs text-[#64748b] mt-0.5">
              Exported {fmtTimestamp(last.downloaded_at)}
              {Array.isArray(last.modules) && last.modules.length > 0 && (
                <> · {last.modules.length} module{last.modules.length > 1 ? "s" : ""}</>
              )}
            </p>
          </div>
          {Array.isArray(last.modules) && (
            <div className="flex flex-wrap gap-1.5">
              {last.modules.map((m) => (
                <Badge
                  key={m}
                  variant="outline"
                  className="bg-white text-[#4338ca] border-[#c7d2fe] text-[10px] uppercase tracking-wide"
                  data-testid={`tally-last-module-${m}`}
                >
                  {m}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-6 space-y-6">
          <div className="text-center space-y-2">
            <div className="h-14 w-14 rounded-2xl bg-[#4338ca]/10 flex items-center justify-center mx-auto">
              <FileCode2 className="h-7 w-7 text-[#4338ca]" />
            </div>
            <h3 className="font-outfit text-lg font-semibold text-[#0f172a]">Tally Prime XML Export</h3>
            <p className="text-sm text-[#64748b] max-w-md mx-auto">
              Choose the modules you want included in the export. Parties get auto-created
              as ledger masters; debit/credit direction uses <code className="text-[#4338ca]">ISDEEMEDPOSITIVE</code>.
            </p>
          </div>

          {/* Module selectors */}
          <div className="grid sm:grid-cols-2 gap-3" data-testid="tally-module-selectors">
            {TALLY_MODULES.map((m) => {
              const Icon = m.icon;
              const isOn = !!selected[m.id];
              const isPurchases = m.id === "purchases";
              return (
                <label
                  key={m.id}
                  htmlFor={`tally-module-${m.id}`}
                  className={`flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-all ${
                    isOn
                      ? "bg-[#eef2ff] border-[#4338ca] shadow-sm"
                      : "bg-white border-[#e2e8f0] hover:border-[#c7d2fe]"
                  }`}
                >
                  <Checkbox
                    id={`tally-module-${m.id}`}
                    checked={isOn}
                    onCheckedChange={() => toggle(m.id)}
                    data-testid={`tally-module-${m.id}`}
                    className="mt-0.5"
                  />
                  <Icon className={`h-4 w-4 mt-0.5 ${isOn ? "text-[#4338ca]" : "text-[#64748b]"}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isOn ? "text-[#0f172a]" : "text-[#475569]"}`}>
                      {m.label}
                    </p>
                    <p className="text-xs text-[#64748b]">{m.desc}</p>
                    {/* Nested toggle: Include Inventory Details (only when
                        Purchases is selected). Drives Item-Invoice vs
                        Service mode on the backend. */}
                    {isPurchases && isOn && (
                      <div
                        className="mt-2.5 pt-2.5 border-t border-[#c7d2fe]/60"
                        onClick={(e) => e.preventDefault()}
                        data-testid="purchases-inventory-toggle-wrapper"
                      >
                        <label
                          htmlFor="purchases-has-inventory"
                          className="flex items-start gap-2 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            id="purchases-has-inventory"
                            checked={purchasesHasInventory}
                            onCheckedChange={(v) => setPurchasesHasInventory(!!v)}
                            data-testid="purchases-has-inventory-toggle"
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-[#0f172a]">
                              Include Inventory Details (Item Invoice Mode)
                            </p>
                            <p className="text-[11px] text-[#64748b] leading-snug">
                              {purchasesHasInventory
                                ? "Emits stock items + Purchase Account allocations (use for goods bills)."
                                : "Emits flat Purchase Account debit only (use for service bills)."}
                            </p>
                          </div>
                        </label>
                      </div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          {/* Period preview + counts */}
          <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-[#64748b]">
            <span className="px-2.5 py-1 rounded-md bg-[#f1f5f9] font-medium text-[#0f172a]">
              {fmtDMY(fromDate)} → {fmtDMY(toDate)}
            </span>
            {data && data.income && (
              <>
                <span>·</span>
                <span>{data.income.invoice_count || 0} invoices in current preview</span>
              </>
            )}
            <span>·</span>
            <span data-testid="tally-selected-count">
              {chosen.length} module{chosen.length === 1 ? "" : "s"} selected
            </span>
          </div>

          {/* Download */}
          <div className="flex justify-center">
            <Button
              onClick={handleDownload}
              disabled={!canDownload}
              data-testid="download-tally-xml"
              className="bg-[#4338ca] hover:bg-[#3730a3] disabled:opacity-50 disabled:cursor-not-allowed rounded-xl px-6 shadow-lg shadow-[#4338ca]/20"
            >
              <Download className="h-4 w-4 mr-2" /> Download Tally XML
            </Button>
          </div>

          <p className="text-xs text-[#94a3b8] text-center">
            Mappings: Items → Stock Items · CGST/SGST/IGST → Duties &amp; Taxes · Sales → Sales Account · Purchases → Purchase Account
          </p>
        </CardContent>
      </Card>

      {/* Recent history */}
      {history.length > 1 && (
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-outfit text-[#0f172a] flex items-center gap-2">
              <History className="h-4 w-4 text-[#4338ca]" /> Recent Exports
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2" data-testid="tally-history-list">
              {history.map((h, idx) => (
                <div
                  key={`${h.downloaded_at}-${idx}`}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 rounded-lg bg-[#f8fafc] border border-[#e2e8f0]"
                  data-testid={`tally-history-row-${idx}`}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-[#0f172a]">
                      {fmtDMY(h.from_date)} → {fmtDMY(h.to_date)}
                    </span>
                    <span className="text-xs text-[#64748b]">{fmtTimestamp(h.downloaded_at)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(h.modules || []).map((m) => (
                      <Badge
                        key={m}
                        variant="outline"
                        className="bg-white text-[#4338ca] border-[#c7d2fe] text-[10px] uppercase"
                      >
                        {m}
                      </Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {historyLoading && history.length === 0 && (
        <p className="text-xs text-center text-[#94a3b8]">Loading export history…</p>
      )}
    </div>
  );
}

/* Shared components */
function TurnoverTrackerPanel({ data }) {
  if (!data) return null;
  const pct = Math.min(data.percentage_services || 0, 100);
  return (
    <div className="space-y-4" data-testid="turnover-tracker">
      <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
        <div className="flex items-center gap-2 mb-4">
          <Gauge className="h-5 w-5 text-[#f59e0b]" />
          <h3 className="font-outfit text-lg font-semibold text-[#0f172a]">FY Turnover Tracker</h3>
        </div>
        <div className="text-3xl font-bold font-mono text-[#0f172a] mb-2">{formatINR(data.total_turnover)}</div>
        <p className="text-sm text-[#64748b] mb-4">{data.invoice_count} invoices since {data.fy_start}</p>
        <div className="space-y-3">
          <div>
            <div className="flex justify-between text-xs text-[#64748b] mb-1">
              <span>Services Threshold (20L)</span>
              <span>{pct.toFixed(0)}%</span>
            </div>
            <div className="h-3 bg-[#f1f5f9] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-[#4338ca]'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-xs text-[#64748b] mb-1">
              <span>Goods Threshold (40L)</span>
              <span>{(data.percentage_goods || 0).toFixed(0)}%</span>
            </div>
            <div className="h-3 bg-[#f1f5f9] rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${(data.percentage_goods||0) >= 100 ? 'bg-red-500' : 'bg-[#10b981]'}`} style={{ width: `${Math.min(data.percentage_goods || 0, 100)}%` }} />
            </div>
          </div>
        </div>
      </div>
      {data.alerts && data.alerts.length > 0 && (
        <div className="space-y-2">
          {data.alerts.map((a, i) => (
            <div key={i} className={`rounded-lg p-4 border flex items-start gap-2 ${a.level === 'critical' ? 'bg-red-50 border-red-200' : a.level === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
              <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${a.level === 'critical' ? 'text-red-500' : a.level === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} />
              <p className="text-sm">{a.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CMP08Panel({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4" data-testid="cmp08-report">
      <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
        <div className="flex items-center gap-2 mb-4">
          <FileJson className="h-5 w-5 text-[#4338ca]" />
          <h3 className="font-outfit text-lg font-semibold text-[#0f172a]">CMP-08 Statement</h3>
          <Badge className="bg-[#4338ca]/10 text-[#4338ca] text-xs">Composition</Badge>
        </div>
        <p className="text-xs text-[#64748b] mb-4">Period: {data.period?.from} to {data.period?.to} | Rate: {data.composition_rate}%</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MetricCard icon={TrendingUp} color="#4338ca" label="Total Turnover" value={formatINR(data.total_turnover)} sub={`${data.invoice_count} invoices`} />
          <MetricCard icon={Scale} color="#f59e0b" label="Tax Liability" value={formatINR(data.tax_liability)} sub={`CGST: ${formatINR(data.cgst_liability)} + SGST: ${formatINR(data.sgst_liability)}`} />
          <MetricCard icon={ArrowRightLeft} color="#10b981" label="RCM Liability" value={formatINR(data.rcm_tax)} sub={`On ${formatINR(data.rcm_amount)} purchases`} />
          <MetricCard icon={Calculator} color="#ef4444" label="Total Payable" value={formatINR(data.total_payable)} />
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800">{data.itc_notice}</p>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, color, label, value, sub, testId }) {
  return (
    <Card className="bg-white border-[#e2e8f0] shadow-sm" data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-[#64748b] font-medium">{label}</p>
          <p className="font-outfit text-lg font-bold text-[#0f172a] truncate">{value}</p>
          {sub && <p className="text-xs text-[#94a3b8]">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function InvoiceTable({ invoices, showGstin, showStatus }) {
  if (!invoices || invoices.length === 0) {
    return <p className="p-8 text-center text-sm text-[#64748b]">No invoices in this period.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
            <th className="text-left py-3 px-4 text-[#64748b] font-medium">Invoice #</th>
            <th className="text-left py-3 px-4 text-[#64748b] font-medium">{showGstin ? "GSTIN" : "Client"}</th>
            <th className="text-left py-3 px-4 text-[#64748b] font-medium">Date</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">Taxable</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">CGST</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">SGST</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">IGST</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">Total</th>
            {showStatus && <th className="text-center py-3 px-4 text-[#64748b] font-medium">Status</th>}
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv, i) => (
            <tr key={i} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]">
              <td className="py-2.5 px-4 font-mono text-xs text-[#4338ca]">{inv.invoice_number}</td>
              <td className="py-2.5 px-4 text-[#0f172a] font-medium text-xs">
                {showGstin ? inv.client_gstin : inv.client_name}
              </td>
              <td className="py-2.5 px-4 text-[#64748b]">{inv.invoice_date || inv.date}</td>
              <td className="py-2.5 px-4 text-right font-mono">{formatINR(inv.taxable_value || inv.subtotal)}</td>
              <td className="py-2.5 px-4 text-right font-mono text-xs">{formatINR(inv.cgst || inv.cgst_amount)}</td>
              <td className="py-2.5 px-4 text-right font-mono text-xs">{formatINR(inv.sgst || inv.sgst_amount)}</td>
              <td className="py-2.5 px-4 text-right font-mono text-xs">{formatINR(inv.igst || inv.igst_amount)}</td>
              <td className="py-2.5 px-4 text-right font-mono font-semibold">{formatINR(inv.total || inv.grand_total)}</td>
              {showStatus && (
                <td className="py-2.5 px-4 text-center">
                  <Badge className={inv.status === "paid" ? "bg-[#10b981] text-white" : inv.status === "sent" ? "bg-[#3b82f6] text-white" : "bg-[#f1f5f9] text-[#64748b]"}>
                    {inv.status}
                  </Badge>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HSNTable({ entries, testId }) {
  if (!entries || entries.length === 0) {
    return <p className="p-6 text-center text-sm text-[#64748b]">No HSN data for this category.</p>;
  }
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
            <th className="text-left py-3 px-4 text-[#64748b] font-medium">HSN/SAC</th>
            <th className="text-left py-3 px-4 text-[#64748b] font-medium">Description</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">Qty</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">Taxable Value</th>
            <th className="text-center py-3 px-4 text-[#64748b] font-medium">GST%</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">CGST</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">SGST</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">IGST</th>
            <th className="text-right py-3 px-4 text-[#64748b] font-medium">Total Tax</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={i} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]">
              <td className="py-2.5 px-4 font-mono text-xs text-[#4338ca] font-medium">{e.hsn_sac}</td>
              <td className="py-2.5 px-4 text-[#0f172a] text-xs">{e.description}</td>
              <td className="py-2.5 px-4 text-right">{e.quantity}</td>
              <td className="py-2.5 px-4 text-right font-mono">{formatINR(e.taxable_value)}</td>
              <td className="py-2.5 px-4 text-center">{e.gst_rate}%</td>
              <td className="py-2.5 px-4 text-right font-mono text-xs">{formatINR(e.cgst)}</td>
              <td className="py-2.5 px-4 text-right font-mono text-xs">{formatINR(e.sgst)}</td>
              <td className="py-2.5 px-4 text-right font-mono text-xs">{formatINR(e.igst)}</td>
              <td className="py-2.5 px-4 text-right font-mono font-semibold">{formatINR(e.total_tax)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function ExportRefundsView({ data, onRefresh }) {
  const [markingId, setMarkingId] = useState(null);

  const markRefundReceived = async (invoiceId) => {
    setMarkingId(invoiceId);
    try {
      await api.put(`/reports/export-refunds/${invoiceId}/mark-received`, {
        refund_date: new Date().toISOString().split("T")[0],
      });
      toast.success("Refund marked as received");
      onRefresh();
    } catch {
      toast.error("Failed to update refund status");
    } finally {
      setMarkingId(null);
    }
  };

  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="export-refunds-view">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white border-[#e2e8f0]">
          <CardContent className="p-4">
            <p className="text-xs text-[#64748b] font-medium uppercase tracking-wider">Export Invoices</p>
            <p className="font-outfit text-2xl font-bold text-[#0f172a] mt-1" data-testid="export-count">{data.invoice_count}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#e2e8f0]">
          <CardContent className="p-4">
            <p className="text-xs text-[#64748b] font-medium uppercase tracking-wider">Total IGST Paid</p>
            <p className="font-outfit text-2xl font-bold text-[#0f172a] mt-1 font-mono" data-testid="total-igst-paid">{formatINR(data.total_igst_paid)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#10b981]/20">
          <CardContent className="p-4">
            <p className="text-xs text-[#10b981] font-medium uppercase tracking-wider">Refunds Received</p>
            <p className="font-outfit text-2xl font-bold text-[#10b981] mt-1 font-mono" data-testid="total-refund-received">{formatINR(data.total_refund_received)}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-[#f59e0b]/20">
          <CardContent className="p-4">
            <p className="text-xs text-[#f59e0b] font-medium uppercase tracking-wider">Pending Refunds</p>
            <p className="font-outfit text-2xl font-bold text-[#f59e0b] mt-1 font-mono" data-testid="total-pending-refund">{formatINR(data.total_pending)}</p>
          </CardContent>
        </Card>
      </div>

      {data.invoice_count === 0 ? (
        <Card className="bg-white border-[#e2e8f0]">
          <CardContent className="p-8 text-center">
            <Globe className="h-10 w-10 text-[#e2e8f0] mx-auto mb-3" />
            <p className="text-sm text-[#64748b]">No export invoices with IGST payment found.</p>
            <p className="text-xs text-[#94a3b8] mt-1">Create an export invoice with "Supply with Payment of IGST" to track refunds here.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-[#e2e8f0] shadow-sm">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Invoice #</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Date</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Client</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Shipping Bill</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Taxable Value</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">IGST Paid</th>
                  <th className="text-center py-3 px-4 text-[#64748b] font-medium">Refund Status</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.invoice_id} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`refund-row-${inv.invoice_id}`}>
                    <td className="py-3 px-4 font-mono text-xs text-[#4338ca] font-medium">{inv.invoice_number}</td>
                    <td className="py-3 px-4 text-[#64748b]">{inv.date}</td>
                    <td className="py-3 px-4 text-[#0f172a]">{inv.client_name}</td>
                    <td className="py-3 px-4 font-mono text-xs text-[#64748b]">{inv.shipping_bill_number || "-"}</td>
                    <td className="py-3 px-4 text-right font-mono text-[#0f172a]">{formatINR(inv.subtotal)}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-[#0f172a]">{formatINR(inv.igst_amount)}</td>
                    <td className="py-3 px-4 text-center">
                      {inv.refund_status === "received" ? (
                        <Badge className="bg-[#10b981]/10 text-[#10b981]">
                          <CheckCircle2 className="h-3 w-3 mr-1 inline" /> Received
                        </Badge>
                      ) : (
                        <Badge className="bg-[#f59e0b]/10 text-[#f59e0b]">Pending</Badge>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {inv.refund_status !== "received" ? (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => markRefundReceived(inv.invoice_id)}
                          disabled={markingId === inv.invoice_id}
                          data-testid={`mark-received-${inv.invoice_id}`}
                          className="text-xs rounded-lg border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10"
                        >
                          {markingId === inv.invoice_id ? "..." : "Mark Received"}
                        </Button>
                      ) : (
                        <span className="text-xs text-[#94a3b8]">{inv.refund_date}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
